/**
 * Strike 3 — Admin Demo Mode toggle + Org Profile editor.
 * Appears as a card inside the admin Settings tab.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ToggleLeft, ToggleRight, AlertTriangle, Building2, Save, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ["Automotive", "FMCG", "Technology", "Pharma", "Financial Services", "Manufacturing", "Consulting", "Healthcare", "Education", "Other"];
const ORG_SIZES = ["<50", "50-200", "200-500", "500-1000", "1000-5000", "5000+"];

function formatRemaining(secs) {
  if (!secs || secs <= 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m remaining`;
}

export function DemoModeCard() {
  const [state, setState] = useState({ active: false, seconds_remaining: 0, ttl_hours: 4 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/demo-mode`, { withCredentials: true });
      setState(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/demo-mode`, { enabled: !state.active }, { withCredentials: true });
      setState({ ...state, ...r.data });
    } catch (err) { alert(err?.response?.data?.detail || "Toggle failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-5" data-testid="demo-mode-card">
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle className={`w-5 h-5 ${state.active ? "text-amber-500" : "text-navy/30"}`} />
        <div className="flex-1">
          <h3 className="font-dm text-navy font-bold text-sm">Demo Mode</h3>
          <p className="font-dm text-navy/60 text-xs mt-0.5">When ON, all flows run normally but no tokens are deducted and no intelligence data is recorded. Use for live demos. Auto-expires after {state.ttl_hours}h.</p>
        </div>
      </div>
      <div className="flex items-center justify-between bg-cream rounded-lg px-4 py-3">
        <div>
          <p className="font-dm text-navy font-semibold text-sm flex items-center gap-2">
            {state.active ? <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" /> : <span className="inline-block w-2 h-2 rounded-full bg-navy/20" />}
            {state.active ? "Active" : "Off"}
          </p>
          {state.active && <p className="font-dm text-navy/60 text-xs mt-0.5">{formatRemaining(state.seconds_remaining)}</p>}
        </div>
        <button onClick={toggle} disabled={busy} data-testid="demo-mode-toggle"
          className={`font-dm font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${state.active ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-navy text-gold hover:bg-navy/90"} disabled:opacity-50`}>
          {state.active ? <><ToggleRight className="w-4 h-4" /> Turn off</> : <><ToggleLeft className="w-4 h-4" /> Turn on</>}
        </button>
      </div>
    </div>
  );
}

export function OrgProfileCard() {
  const [profile, setProfile] = useState({ industry: "", org_size: "", primary_sector: "", hq_location: "" });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/org/profile`, { withCredentials: true });
      setProfile({
        industry: r.data?.industry || "",
        org_size: r.data?.org_size || "",
        primary_sector: r.data?.primary_sector || "",
        hq_location: r.data?.hq_location || "",
      });
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/org/profile`, profile, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { alert(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const complete = profile.industry && profile.org_size;

  return (
    <div className="bg-white rounded-xl shadow-md p-5" data-testid="org-profile-card">
      <div className="flex items-start gap-3 mb-4">
        <Building2 className="w-5 h-5 text-gold" />
        <div className="flex-1">
          <h3 className="font-dm text-navy font-bold text-sm flex items-center gap-2">
            Organisation Profile
            {complete ? <span className="inline-flex items-center gap-1 text-green-600 font-normal text-[10px]"><CheckCircle className="w-3 h-3" /> complete</span>
              : <span className="text-amber-600 font-normal text-[10px]">— incomplete</span>}
          </h3>
          <p className="font-dm text-navy/60 text-xs mt-0.5">Used for industry benchmarks when intelligence thresholds are reached. Anonymised — never shared with other orgs.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="font-dm text-navy/70 text-xs font-bold">Industry</label>
          <select value={profile.industry} onChange={e => setProfile({ ...profile, industry: e.target.value })} data-testid="org-industry"
            className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
            <option value="">— select —</option>
            {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div>
          <label className="font-dm text-navy/70 text-xs font-bold">Organisation size</label>
          <select value={profile.org_size} onChange={e => setProfile({ ...profile, org_size: e.target.value })} data-testid="org-size"
            className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
            <option value="">— select —</option>
            {ORG_SIZES.map(s => <option key={s} value={s}>{s} people</option>)}
          </select>
        </div>
        <div>
          <label className="font-dm text-navy/70 text-xs font-bold">Primary sector (optional)</label>
          <input value={profile.primary_sector} onChange={e => setProfile({ ...profile, primary_sector: e.target.value })}
            placeholder="e.g. SaaS, EV Manufacturing, Specialty Pharma"
            className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
        </div>
        <div>
          <label className="font-dm text-navy/70 text-xs font-bold">HQ location (city)</label>
          <input value={profile.hq_location} onChange={e => setProfile({ ...profile, hq_location: e.target.value })}
            placeholder="e.g. Mumbai"
            className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 mt-4">
        {saved && <span className="font-dm text-green-600 text-xs font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Saved</span>}
        <button onClick={save} disabled={busy} data-testid="org-profile-save"
          className="bg-navy text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg hover:bg-navy/90 disabled:opacity-50 flex items-center gap-2">
          <Save className="w-3 h-3" /> Save Profile
        </button>
      </div>
    </div>
  );
}

export default function AdminSettingsStrike3() {
  return (
    <div className="space-y-6" data-testid="admin-settings-strike3">
      <OrgProfileCard />
    </div>
  );
}
